import type { FrameSize, NavRepo, NavSection } from "../shell.ts";
import type { Theme } from "../theme.ts";
import { EMPTY_FORMATION_ART } from "../components/feedback.ts";
import { html, raw, type Html } from "../html.ts";
import { REPO, REPOS, RUN, USER } from "./fixtures.ts";

export type ScreenState = "populated" | "empty" | "loading" | "error" | "success";

/**
 * The `botallion` npm package is unpublished (post-launch, 7 Oct 2026).
 * Typed as boolean so the command markup stays in the build and can be switched back on.
 */
export const showConnectCommand: boolean = false;

/** Who and where a screen is about. Omitted fields stay the design-board fixtures, so the gallery is unchanged. */
export interface Place {
  repo?: { org?: string; name?: string; url?: string; branch?: string };
  user?: { firstName?: string; initials?: string; settingsHref?: string };
  repos?: readonly NavRepo[];
  run?: { id?: string; title?: string; cap?: string; estimate?: string };
  /** Plan awaiting approval, so the approve button names it. */
  specId?: string;
  hrefs?: Partial<Record<NavSection, string>>;
}

/** Options every screen takes. `frame` fixes the size for side-by-side review with the design boards. */
export interface ScreenOptions {
  state?: ScreenState;
  theme?: Theme | undefined;
  frame?: FrameSize | undefined;
  /** Desktop only, when the app is already choosing the layout. The gallery renders both. */
  device?: "desktop" | undefined;
  place?: Place | undefined;
}

/** Fixture place, with anything the app passed laid over it. */
export function placeOf(opts: ScreenOptions = {}) {
  return {
    repo: { ...REPO, ...opts.place?.repo },
    user: { firstName: USER.firstName, initials: USER.initials, settingsHref: "/settings" as string | undefined, ...opts.place?.user },
    repos: opts.place?.repos ?? REPOS.map((r) => ({ name: r.name, active: "active" in r ? r.active : false })),
    run: { ...RUN, ...opts.place?.run },
    specId: opts.place?.specId,
    hrefs: opts.place?.hrefs,
  };
}

export const DESKTOP_FRAME: FrameSize = { width: 1280, height: 800 };
export const WIDE_FRAME: FrameSize = { width: 1440, height: 900 };

/** The design's empty-formation art for waiting and zero states (heads as outlines, one orange antenna dot). */
export const FORMATION_ART: Html = raw(
  '<svg class="bt-empty__art" viewBox="0 0 96 80" aria-hidden="true" focusable="false"><rect class="o" x="6" y="12" width="36" height="26" rx="8"></rect><rect class="o" x="54" y="12" width="36" height="26" rx="8"></rect><rect class="o" x="24" y="34" width="48" height="40" rx="14" style="fill: var(--bg-000);"></rect><rect class="v" x="31" y="45" width="34" height="16" rx="8"></rect><circle class="a" cx="48" cy="24" r="4"></circle></svg>',
);

export { EMPTY_FORMATION_ART };

export const heading = (text: string, size = 30, line = 36, weight = 600): Html =>
  html`<h1 class="disp" style="font-size: ${size}px; line-height: ${line}px; font-weight: ${weight};">${text}</h1>`;
